import React from 'react';
import Box from '@mui/system/Box';
import { useStacklineTheme } from '@stackline/ui';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { useLocation } from 'src/utils/Hooks/typedHooks';
import AddEditAlert from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/common/AddEditAlert';
import AlertTables from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/common/AlertTables';
import {
  AlertsTab,
  AlertsTabsDisplayName,
  AlertsTabsPath
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/utils';
import AlertTemplatesPage from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/common/AlertTemplatesPage';

interface AlertsNavigationTabsProps {
  tab: AlertsTab;
}

export const AlertsNavigationTabs = ({ tab }: AlertsNavigationTabsProps) => {
  const { beaconNavigator } = useBeaconNavigator();

  return (
    <GenericNavigationTabs
      tab={tab}
      tabs={[
        {
          label: AlertsTabsDisplayName.myAlerts,
          onClick: () => {
            beaconNavigator.setPathname(AlertsTabsPath.myAlerts);
            beaconNavigator.go();
          },
          value: AlertsTab.MyAlerts
        },
        {
          label: AlertsTabsDisplayName.teamAlerts,
          onClick: () => {
            beaconNavigator.setPathname(AlertsTabsPath.teamAlerts);
            beaconNavigator.go();
          },
          value: AlertsTab.TeamAlerts
        }
      ]}
    />
  );
};

const CreateAlertButton = () => {
  const { beaconNavigator } = useBeaconNavigator();

  const handleCreateAlert = () => {
    beaconNavigator.setPathname(AlertsTabsPath.templateAlert);
    beaconNavigator.go();
  };

  return (
    <SlButton variant="contained" onClick={handleCreateAlert}>
      + Create Alert
    </SlButton>
  );
};

const AlertsTopNav = () => {
  return (
    <Flex justifyContent="space-between" flexDirection="row" alignItems="center">
      <Text variant="h2">Alert Builder</Text>
      <CreateAlertButton />
    </Flex>
  );
};

// Removed MyAlertsContent and TeamAlertsContent - AlertTables now auto-detects based on pathname

const BeaconAlertsPageLayout: React.FC = () => {
  const theme = useStacklineTheme();
  const location = useLocation();

  if (location.pathname === AlertsTabsPath.templateAlert) {
    return <AlertTemplatesPage />;
  }

  // If we're on the create or edit alert page, render the AddEditAlert component
  const isCreateAlert = location.pathname === AlertsTabsPath.createAlert;
  const isEditAlert = location.pathname === AlertsTabsPath.editAlert;

  if (isCreateAlert || isEditAlert) {
    // AddEditAlert now handles all template logic internally
    return <AddEditAlert />;
  }

  const getCurrentTab = (pathname: string): AlertsTab => {
    if (pathname === AlertsTabsPath.teamAlerts) {
      return AlertsTab.TeamAlerts;
    }
    return AlertsTab.MyAlerts;
  };

  return (
    <Box>
      <AlertsTopNav />
      <Box marginBottom={theme.spacing.lg}>
        <AlertsNavigationTabs tab={getCurrentTab(location.pathname)} />
        <BasePageContainer>
          <AlertTables />
        </BasePageContainer>
      </Box>
    </Box>
  );
};

export default BeaconAlertsPageLayout;
